import { setPageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, Wifi, Users, Radio, MonitorPlay, Zap, PhoneCall, Camera } from "lucide-react";
import { Broadcast } from "@/components/wve/Broadcast";
import { PageCTA } from "@/components/wve/PageCTA";
import { EnquiryModal } from "@/components/wve/EnquiryModal";

const chips = [
  { icon: Camera, label: "Multi-Camera Production" },
  { icon: MonitorPlay, label: "Teams · Zoom · YouTube" },
  { icon: Users, label: "Hybrid Events" },
  { icon: Video, label: "Vision Mixing" },
];

const heroCards = [
  { icon: Radio, iconBg: "bg-[#E80042]", big: "LIVE Now", small: "Real-time streaming" },
  { icon: Camera, iconBg: "bg-white/15", big: "12× Cameras", small: "Blackmagic fleet" },
  { icon: Zap, iconBg: "bg-white/15", big: "Wired + 5G", small: "Failover internet" },
  { icon: PhoneCall, iconBg: "bg-[#E80042]", big: "1 Contact", small: "From cue to credit" },
];

const deliverables = [
  { title: "Camera Capture", desc: "Multi-camera setups from our fleet of 12× Blackmagic cameras — wide locked-off plus operated, all colour-matched." },
  { title: "Vision Mixing", desc: "Live-switched programme output through ATEM broadcast vision mixers." },
  { title: "Polished Broadcast Output", desc: "Dedicated host account setup, programme output via capture card, clean audio routing and PIP overlays — streamed to Teams, Zoom, YouTube or your webinar platform, all managed live." },
  { title: "In-Room Experience", desc: "Display content management, full PA & comms, confidence monitors for talent, room dressing and acoustic treatment." },
  { title: "Remote Experience", desc: "Remote participants receive the same high-quality experience as those in the room." },
  { title: "Show Calling", desc: "Event coordination and cue management from first rehearsal to final credit." },
];

export default function LivePage() {
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Live Streaming & Hybrid Broadcast Production | WVE Studios",
      "Broadcast-quality live streaming & hybrid event production across the UK — 12× Blackmagic cameras, vision mixing and output to Teams, Zoom, YouTube or webinar. Based in Essex.",
      "/live"
    );
  }, []);

  return (
    <main data-testid="live-page">
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-20 relative overflow-hidden">
        <span className="absolute -right-16 top-0 font-display text-[20rem] leading-none text-white/[0.05] select-none hidden lg:block">LIVE</span>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative grid lg:grid-cols-12 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2.5 bg-[#E80042] rounded-full px-5 py-2 text-xs sm:text-sm uppercase tracking-[0.2em] font-bold" data-testid="live-hero-badge">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              Live & Hybrid Production
            </span>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight mt-8">
              Live. Hybrid.<br /><span className="text-[#E80042]">Broadcast-ready.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed">
              From multi-camera setups to social media LIVE — high-quality, reliable live production and hybrid broadcasting across the UK, polished on-site and broadcast-ready online.
            </p>
            <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              {chips.map((c) => (
                <div key={c.label} className="bg-white/5 border border-white/10 rounded-lg px-4 py-5 text-center hover:border-[#E80042] hover:-translate-y-0.5 transition-[border-color,transform] duration-300">
                  <c.icon className="w-5 h-5 mx-auto text-[#E80042]" />
                  <p className="text-xs sm:text-sm font-semibold mt-3 leading-snug">{c.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                onClick={() => setBookOpen(true)}
                data-testid="live-book-button"
                className="inline-flex items-center gap-3 bg-white text-[#0A0A0A] font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-[#E80042] hover:text-white transition-colors duration-300 active:scale-95"
              >
                Book a Live Stream <Wifi className="w-5 h-5" />
              </button>
              <Link
                to="/services"
                data-testid="live-view-services-link"
                className="border border-white/30 text-white font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-white/10 transition-colors duration-300"
              >
                View All Services
              </Link>
            </div>
          </motion.div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {heroCards.map((c, i) => (
              <motion.div
                key={c.big}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.12 }}
                className="bg-white/5 border border-white/10 backdrop-blur rounded-lg p-6 sm:p-7 hover:border-[#E80042] hover:-translate-y-1 transition-[border-color,transform] duration-300"
                data-testid={`live-hero-card-${i + 1}`}
              >
                <span className={`w-12 h-12 rounded-full ${c.iconBg} flex items-center justify-center`}>
                  <c.icon className="w-5 h-5 text-white" />
                </span>
                <p className="font-display uppercase text-2xl mt-5 tracking-tight">{c.big}</p>
                <p className="text-white/60 text-sm mt-1">{c.small}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <EnquiryModal open={bookOpen} onClose={() => setBookOpen(false)} context="Live Stream Booking" />
      </section>

      <section className="py-20 sm:py-28 bg-white border-b border-black/10" data-testid="live-streaming-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">Live Streams</p>
            <h2 className="font-grotesk font-extrabold text-4xl sm:text-5xl tracking-tight leading-none">
              Connect with your audience, <span className="text-[#E80042]">wherever they are.</span>
            </h2>
            <div className="mt-6 space-y-4 text-[#555555] text-lg leading-relaxed">
              <p>We offer a complete live streaming package so you can connect directly to your customers, colleagues, target audience and industry peers wherever they might be.</p>
              <p>We have years of experience in live event streaming — Awards Ceremonies, Film Premieres, Corporate Xmas Parties, Live Festival Streaming and so much more!</p>
              <p>Broadcast to Microsoft Teams, Zoom, YouTube or your webinar platform of choice. We also offer a bespoke service to a dedicated secure page on your website, branded with your corporate identity — so you have total control of who watches.</p>
            </div>
            <div className="flex flex-wrap gap-2 mt-6">
              {["Teams", "Zoom", "YouTube", "Webinar"].map((p) => (
                <span key={p} className="border border-black/15 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold">
                  {p}
                </span>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-7 space-y-5">
            <div className="aspect-video rounded-lg overflow-hidden bg-[#0A0A0A]" data-testid="live-video-embed">
              <iframe
                src="https://www.youtube.com/embed/BCm65EtKLwE?rel=0"
                title="Event Live Stream — WVE Studios"
                className="w-full h-full border-0"
                allow="autoplay; fullscreen; encrypted-media"
              />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <img loading="lazy" src="https://customer-assets-rejwkqb3.emergentagent.net/job_design-breakthrough/artifacts/jndjrmb8_IMG_3030.webp" alt="WVE broadcast multiview at a live F1 event" className="w-full h-[220px] object-cover rounded-lg" />
              <img loading="lazy" src="https://customer-assets-rejwkqb3.emergentagent.net/job_design-breakthrough/artifacts/iixbbl6u_IMG_3031.webp" alt="WVE vision mixer operating a live broadcast" className="w-full h-[220px] object-cover rounded-lg" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="live-deliverables-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-14">
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">End-to-End</p>
            <h2 className="font-grotesk font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
              Everything managed, <span className="text-[#E80042]">from cue one.</span>
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {deliverables.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="bg-white border border-black/10 rounded-lg p-7 hover:-translate-y-1 hover:shadow-xl hover:border-[#E80042] transition-[transform,box-shadow,border-color] duration-300"
                data-testid={`live-deliverable-${i + 1}`}
              >
                <span className="font-display text-[#E80042] text-lg">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display uppercase text-2xl mt-3 tracking-tight">{d.title}</h3>
                <p className="text-[#555555] text-sm leading-relaxed mt-3">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Broadcast />

      <PageCTA context="Live & Hybrid Production" title="Go live with us." subtitle="Wired + 5G failover, full crew and every cable included." />
    </main>
  );
}
